import React, { useRef, useState } from "react";
import { useLang } from "../contexts/LanguageContext";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";

const VIDEO_URL =
  "https://customer-assets.emergentagent.com/job_modern-refinery/artifacts/a8aa7gwp_AQPoohUFBQEQ93zyGBBDRClzaK-sQGOvPAP0g5tSYJXm1ykFoQ0Qx_DpxyxvX3EuIOCebmY-sBGP40w27s3FdYt1NTz3NhvhFsEvGKE_MfaRgA.mp4";

const VideoSection = () => {
  const { t } = useLang();
  const v = t.video;
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);

  const togglePlay = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) {
      el.play();
      setPlaying(true);
    } else {
      el.pause();
      setPlaying(false);
    }
  };

  const toggleMute = () => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setMuted(el.muted);
  };

  return (
    <section
      id="video"
      data-testid="video-section"
      className="relative bg-[#1A201C] text-[#FDFBF7] py-20 md:py-28 overflow-hidden"
    >
      {/* Subtle dotted pattern */}
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "radial-gradient(circle, #DE9E36 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header — text above video */}
        <div className="max-w-3xl mb-12 md:mb-16 reveal">
          <div className="flex items-center gap-3 mb-6">
            <span className="divider-line" />
            <span className="overline text-[#DE9E36]">{v.overline}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl text-white font-bold leading-[1.1] tracking-tight">
            {v.title}
          </h2>
          <p className="mt-6 text-base sm:text-lg text-white/75 leading-relaxed max-w-2xl">
            {v.subtitle}
          </p>
        </div>

        {/* Video — full width, fully visible (object-contain) */}
        <div className="reveal relative">
          <div
            className="relative w-full bg-black border border-white/10 overflow-hidden flex items-center justify-center"
            style={{ minHeight: "60vh" }}
          >
            <video
              ref={videoRef}
              src={VIDEO_URL}
              data-testid="ekobiotek-video"
              className="w-full h-full max-h-[85vh] object-contain"
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
            />

            {/* Play / mute controls overlay */}
            <div className="absolute bottom-5 right-5 flex items-center gap-3 z-10">
              <button
                type="button"
                onClick={togglePlay}
                data-testid="video-toggle-play"
                aria-label="Play / pause"
                className="w-12 h-12 rounded-full bg-[#DE9E36]/95 backdrop-blur-md text-[#1A201C] flex items-center justify-center hover:bg-[#FDFBF7] transition-colors shadow-lg"
              >
                {playing ? (
                  <Pause size={18} fill="currentColor" />
                ) : (
                  <Play size={18} fill="currentColor" />
                )}
              </button>
              <button
                type="button"
                onClick={toggleMute}
                data-testid="video-toggle-mute"
                aria-label="Mute / unmute"
                className="w-12 h-12 rounded-full bg-black/55 backdrop-blur-md border border-white/30 text-white flex items-center justify-center hover:border-[#DE9E36] hover:text-[#DE9E36] transition-colors"
              >
                {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
              </button>
            </div>

            {/* Top-left badge */}
            <div className="absolute top-5 left-5 z-10 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/55 backdrop-blur-md border border-white/15">
              <span className="w-2 h-2 rounded-full bg-[#DE9E36] animate-pulse" />
              <span className="overline text-white/85">EKOBIOTEK · Live</span>
            </div>
          </div>

          {/* Hint */}
          <div className="mt-5 flex items-center justify-between gap-4 flex-wrap">
            <span className="overline text-white/55">
              {muted ? v.unmuteHint : v.playingLabel}
            </span>
            <span className="overline text-white/35">EKOBIOTEK-UKRAINA · Kherson</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VideoSection;
